/**
 * Computes photon-based percentage distribution of the final mixed SPD
 * across wavelength bands using trapezoidal integration.
 * Returns null if required columns are missing or total is 0.
 *
 * @param {Object} template - SPD template with wl, photonWeightNm (optional), band masks
 * @param {number[]} spdMix - mixed SPD values (same length as template.wl)
 * @returns {{ uvPct, bluePct, greenPct, redPct, farRedPct } | null}
 */
export function computeBandPercentages(template, spdMix) {
  const wl = Array.isArray(template?.wl) ? template.wl : [];
  const n = wl.length;
  if (!n || !Array.isArray(spdMix) || spdMix.length !== n) {
    return null;
  }

  const photonWeight = Array.isArray(template?.photonWeightNm) && template.photonWeightNm.length === n
    ? template.photonWeightNm
    : wl;
  const bandUV = template?.bandUV;
  const bandBlue = template?.bandBlue;
  const bandGreen = template?.bandGreen;
  const bandRed = template?.bandRed;
  const bandFarRed = template?.bandFarRed;

  const hasAllBands =
    Array.isArray(bandUV) && bandUV.length === n &&
    Array.isArray(bandBlue) && bandBlue.length === n &&
    Array.isArray(bandGreen) && bandGreen.length === n &&
    Array.isArray(bandRed) && bandRed.length === n &&
    Array.isArray(bandFarRed) && bandFarRed.length === n;
  if (!hasAllBands) {
    return null;
  }

  const p = spdMix.map((val, i) => (Number(val) || 0) * (Number(photonWeight[i]) || 0));

  let uv = 0;
  let blue = 0;
  let green = 0;
  let red = 0;
  let farRed = 0;

  for (let i = 0; i < n - 1; i += 1) {
    const dl = (Number(wl[i + 1]) || 0) - (Number(wl[i]) || 0);
    const pAvg = (p[i] + p[i + 1]) / 2;
    const mUV = ((bandUV[i] === 1 ? 1 : 0) + (bandUV[i + 1] === 1 ? 1 : 0)) / 2;
    const mBlue = ((bandBlue[i] === 1 ? 1 : 0) + (bandBlue[i + 1] === 1 ? 1 : 0)) / 2;
    const mGreen = ((bandGreen[i] === 1 ? 1 : 0) + (bandGreen[i + 1] === 1 ? 1 : 0)) / 2;
    const mRed = ((bandRed[i] === 1 ? 1 : 0) + (bandRed[i + 1] === 1 ? 1 : 0)) / 2;
    const mFarRed = ((bandFarRed[i] === 1 ? 1 : 0) + (bandFarRed[i + 1] === 1 ? 1 : 0)) / 2;

    uv += pAvg * mUV * dl;
    blue += pAvg * mBlue * dl;
    green += pAvg * mGreen * dl;
    red += pAvg * mRed * dl;
    farRed += pAvg * mFarRed * dl;
  }

  const total = uv + blue + green + red + farRed;
  if (!total || !Number.isFinite(total)) {
    return null;
  }

  return {
    uvPct: (100 * uv) / total,
    bluePct: (100 * blue) / total,
    greenPct: (100 * green) / total,
    redPct: (100 * red) / total,
    farRedPct: (100 * farRed) / total,
  };
}
